import { UserOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Avatar, Card, Space, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useEmployeePhotoUrl } from "../hooks/useEmployees";
import { type EmployeeDetail, HR_STATUS_COLORS, hrStatusLabel } from "../types";
import { EmployeeFactsRow } from "./EmployeeFactsRow";
import { EmployeeHeaderActions } from "./EmployeeHeaderActions";

const { Text, Title } = Typography;

export interface EmployeeProfileHeaderProps {
	employee: EmployeeDetail;
	departmentName?: string;
	positionName?: string;
	managerName?: string;
}

/**
 * Bir fakt ustuni — yorliq ustida, qiymat ostida.
 *
 * ⚠️ Bo'sh qiymat `—` bilan chiziladi, ustunning O'ZI olib tashlanmaydi:
 * to'rt ustunning kengligi qatordan qatorga sakrasa, ko'z ularni
 * solishtira olmasdi.
 */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="min-w-0">
			<div className="text-xs text-[#8a929e] mb-1">{label}</div>
			<div className="text-sm text-[#14171c] truncate">{children}</div>
		</div>
	);
}

/**
 * Xodim profilining yuqori kartasi (dizayn kanvasi, «Xodim profili»).
 *
 * ⚠️ **Rasm `<img src>` bilan TO'G'RIDAN-TO'G'RI olinmaydi** — access token
 * faqat JS xotirasida va brauzerning o'z rasm so'rovi `Authorization`
 * sarlavhasini olib chiqmaydi. `useEmployeePhotoUrl` blob URL ini
 * yaratadi va unmount'da `revokeObjectURL` qiladi.
 *
 * ⚠️ **Faktlar qatori TO'RT ustun** — «Hududlar» va «Qurilma» ustunlari
 * ATAYLAB yo'q: `GET /devices` da query filtri, xodim→hudud teskari
 * qidiruvi esa umuman yo'q (reja, «Aniqlangan bo'shliqlar»). Bo'sh yoki
 * soxta qiymatli ustun chizilmaydi.
 *
 * ⚠️ **Bo'lim va lavozim havolalari RO'YXAT sahifasiga boradi**, obyekt
 * profiliga emas: bo'lim va lavozim profillari 4-rejada. Rahbar esa
 * HAQIQIY obyektga boradi — `/employees/$id` marshruti mavjud.
 *
 * ⚠️⚠️ **Havola endi FILTRLANGAN ro'yxatga boradi.** Ilgari bu yerda
 * «`EmployeesPage` filtrlari URL'da saqlanmaydi (lokal `useState`), ya'ni
 * `?departmentId=` bugun hech narsani filtrlamasdi» degan qarz yozilgan
 * edi va shuning uchun havolalar quruq `/departments` · `/positions` ga
 * borardi. `useEmployeeFilters` (`hooks/useEmployeeFilters.ts`) filtrni
 * URL ga ko'chirgach qarz YOPILDI.
 *
 * ⚠️ Manzil `search` PROPI orqali beriladi — `to="/employees?departmentId=…"`
 * ISHLAMAYDI: `@tanstack/router-core` `to` ni to'g'ridan-to'g'ri PATHNAME
 * sifatida o'qiydi (`resolvePathWithBase`) va `?` bo'yicha BO'LMAYDI;
 * natijaviy qidiruv satri alohida manbadan (`dest.search`) keladi.
 *
 * ⚠️ Havola FAQAT id VA nom birga bo'lganda chiziladi: nomsiz havola
 * o'quvchiga nima ochilishini aytmasdi, idsiz esa filtrsiz ro'yxatga
 * olib borardi.
 */
export function EmployeeProfileHeader({
	employee,
	departmentName,
	positionName,
	managerName,
}: EmployeeProfileHeaderProps) {
	const { t } = useTranslation("employees");
	const photoUrl = useEmployeePhotoUrl(employee.id, employee.photoFileId);

	return (
		<Card>
			<div className="flex items-start gap-4">
				<Avatar
					size={76}
					src={photoUrl ?? undefined}
					icon={<UserOutlined />}
					alt={employee.fullName}
				/>

				<div className="min-w-0 flex-1">
					<div className="flex items-start gap-4">
						<Space size="small" wrap align="center" className="min-w-0 flex-1">
							<Title level={4} className="!mb-0 !text-[22px]">
								{employee.fullName}
							</Title>

							<Tag color={HR_STATUS_COLORS[employee.hrStatus]}>
								{hrStatusLabel(t, employee.hrStatus)}
							</Tag>

							{/* ⚠️ Yorliq FAQAT bayroq ostida — ball har xodimda bor va
							    uni doim ko'rsatish «xavf» so'zini ma'nosizlantirardi. */}
							{employee.riskFlag ? (
								<Tag color="red">
									{t("profileHeader.riskScoreLabel", { soni: employee.riskScore })}
								</Tag>
							) : null}
						</Space>

						<EmployeeHeaderActions employee={employee} />
					</div>

					<div className="mt-1">
						<Text type="secondary" className="text-xs">
							{employee.phone}
						</Text>
					</div>

					<div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
						<Fact label={t("profileHeader.department")}>
							{employee.departmentId && departmentName ? (
								<Link
									to="/employees"
									search={{ departmentId: employee.departmentId }}
									className="text-[#1c5cab] hover:underline"
								>
									{departmentName}
								</Link>
							) : (
								"—"
							)}
						</Fact>

						<Fact label={t("profileHeader.position")}>
							{employee.positionId && positionName ? (
								<Link
									to="/employees"
									search={{ positionId: employee.positionId }}
									className="text-[#1c5cab] hover:underline"
								>
									{positionName}
								</Link>
							) : (
								"—"
							)}
						</Fact>

						<Fact label={t("profileHeader.manager")}>
							{employee.managerId && managerName ? (
								<Link
									to="/employees/$id"
									params={{ id: employee.managerId }}
									className="text-[#1c5cab] hover:underline"
								>
									{managerName}
								</Link>
							) : (
								"—"
							)}
						</Fact>

						<Fact label={t("profileHeader.idCode")}>{employee.idCode || "—"}</Fact>
					</div>

					<EmployeeFactsRow employee={employee} />
				</div>
			</div>
		</Card>
	);
}
